import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import menuService from "../../../services/menuService";
import { showToast } from "../../../utils/showToast";

const buildOrderValue = (value) => {
  const next = Number(value);
  if (!Number.isFinite(next)) return 0;
  return Math.max(0, Math.trunc(next));
};

const sortByOrder = (items = []) =>
  [...items].sort((a, b) => {
    const aOrder = a.orden ?? 0;
    const bOrder = b.orden ?? 0;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (a.nombre || "").localeCompare(b.nombre || "");
  });

const getParentsFrom = (list = []) => sortByOrder(list.filter((item) => !item.parent_id));
const getChildrenFrom = (list = [], parentId) =>
  sortByOrder(list.filter((item) => item.parent_id === parentId));

const reorderList = (list, draggedId, targetId) => {
  if (!draggedId || draggedId === targetId) return list;
  const fromIndex = list.findIndex((item) => item.id === draggedId);
  if (fromIndex < 0) return list;
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  if (!targetId) {
    next.push(moved);
    return next;
  }
  const toIndex = next.findIndex((item) => item.id === targetId);
  if (toIndex < 0) {
    next.push(moved);
    return next;
  }
  next.splice(toIndex, 0, moved);
  return next;
};

export default function MenuConfig() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragState, setDragState] = useState({ id: null, type: null });
  const dragRef = useRef({ id: null, parentId: null, type: null });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await menuService.listarAdmin();
        if (!mounted) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error cargando menus:", error);
        showToast({ type: "danger", message: "No se pudieron cargar los menus." });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const parents = useMemo(() => getParentsFrom(items), [items]);
  const childrenMap = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      if (!item.parent_id) return;
      const list = map.get(item.parent_id) || [];
      list.push(item);
      map.set(item.parent_id, list);
    });
    map.forEach((list, key) => {
      map.set(key, sortByOrder(list));
    });
    return map;
  }, [items]);

  const handleDragStart = (event, id, parentId, type) => {
    event.stopPropagation();
    event.dataTransfer.setData("text/plain", String(id));
    event.dataTransfer.effectAllowed = "move";
    dragRef.current = {
      id,
      parentId,
      type,
    };
    setDragState({ id, type });
  };

  const resetDrag = () => {
    dragRef.current = { id: null, parentId: null, type: null };
    setDragState({ id: null, type: null });
  };

  const handleDropOnParent = (targetId) => {
    const { id: draggedId, type } = dragRef.current;
    if (!draggedId || type !== "parent") return;
    setItems((prev) => {
      const parentList = getParentsFrom(prev);
      const nextList = reorderList(parentList, draggedId, targetId);
      const orderMap = new Map(nextList.map((item, idx) => [item.id, idx + 1]));
      return prev.map((item) => {
        if (item.parent_id) return item;
        const nextOrder = orderMap.get(item.id);
        return nextOrder ? { ...item, orden: nextOrder } : item;
      });
    });
    resetDrag();
  };

  const handleDropOnGroup = (targetParentId) => {
    const { id: draggedId, parentId: sourceParentId, type } = dragRef.current;
    if (!draggedId || type !== "child") return;
    if (!sourceParentId || !targetParentId) return;
    setItems((prev) => {
      const sourceList = getChildrenFrom(prev, sourceParentId).filter(
        (item) => item.id !== draggedId
      );
      const targetList = getChildrenFrom(prev, targetParentId);
      const movedItem = prev.find((item) => item.id === draggedId);
      if (!movedItem) return prev;

      const nextTarget = [...targetList, { ...movedItem, parent_id: targetParentId }];
      const sourceOrderMap = new Map(sourceList.map((item, idx) => [item.id, idx + 1]));
      const targetOrderMap = new Map(nextTarget.map((item, idx) => [item.id, idx + 1]));

      return prev.map((item) => {
        if (item.id === draggedId) {
          return {
            ...item,
            parent_id: targetParentId,
            orden: targetOrderMap.get(item.id) ?? item.orden,
          };
        }
        if (item.parent_id === sourceParentId) {
          const nextOrder = sourceOrderMap.get(item.id);
          return nextOrder ? { ...item, orden: nextOrder } : item;
        }
        if (item.parent_id === targetParentId) {
          const nextOrder = targetOrderMap.get(item.id);
          return nextOrder ? { ...item, orden: nextOrder } : item;
        }
        return item;
      });
    });
    resetDrag();
  };

  const handleDropOnChild = (targetParentId, targetId) => {
    const { id: draggedId, parentId: sourceParentId, type } = dragRef.current;
    if (!draggedId || type !== "child" || !targetParentId) return;
    setItems((prev) => {
      if (!sourceParentId) return prev;
      if (sourceParentId === targetParentId) {
        const list = getChildrenFrom(prev, sourceParentId);
        const nextList = reorderList(list, draggedId, targetId);
        const orderMap = new Map(nextList.map((item, idx) => [item.id, idx + 1]));
        return prev.map((item) => {
          if (item.parent_id !== sourceParentId) return item;
          const nextOrder = orderMap.get(item.id);
          return nextOrder ? { ...item, orden: nextOrder } : item;
        });
      }

      const sourceList = getChildrenFrom(prev, sourceParentId).filter(
        (item) => item.id !== draggedId
      );
      const targetList = getChildrenFrom(prev, targetParentId);
      const movedItem = prev.find((item) => item.id === draggedId);
      if (!movedItem) return prev;

      const nextTarget = reorderList(
        [...targetList, { ...movedItem, parent_id: targetParentId }],
        draggedId,
        targetId
      );
      const sourceOrderMap = new Map(sourceList.map((item, idx) => [item.id, idx + 1]));
      const targetOrderMap = new Map(nextTarget.map((item, idx) => [item.id, idx + 1]));

      return prev.map((item) => {
        if (item.id === draggedId) {
          return {
            ...item,
            parent_id: targetParentId,
            orden: targetOrderMap.get(item.id) ?? item.orden,
          };
        }
        if (item.parent_id === sourceParentId) {
          const nextOrder = sourceOrderMap.get(item.id);
          return nextOrder ? { ...item, orden: nextOrder } : item;
        }
        if (item.parent_id === targetParentId) {
          const nextOrder = targetOrderMap.get(item.id);
          return nextOrder ? { ...item, orden: nextOrder } : item;
        }
        return item;
      });
    });
    resetDrag();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleSave = async () => {
    if (!items.length) return;
    setSaving(true);
    try {
      const payload = items.map((item) => ({
        id: item.id,
        orden: buildOrderValue(item.orden),
        parent_id: item.parent_id ?? null,
      }));
      await menuService.actualizarOrden(payload);
      showToast({ type: "success", message: "Orden actualizado." });
    } catch (error) {
      console.error("Error guardando orden:", error);
      showToast({ type: "danger", message: "No se pudo guardar el orden." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="management-page management-theme-teal configuracion-wrapper">
      <div className="container-fluid">
        <div className="management-card management-header mb-4">
          <div className="d-flex align-items-start flex-wrap gap-2 w-100">
            <div className="flex-grow-1">
              <Link to="/dashboard/configuracion" className="text-muted text-decoration-none small d-inline-block mb-1">
                <i className="bi bi-chevron-left me-1"></i>Configuración
              </Link>
              <div className="dashboard-eyebrow">Configuración / Menú</div>
              <h5 className="mb-1">Orden del menu</h5>
              <p className="text-muted mb-0">
                Arrastra los modulos y submodulos para ordenar o mover entre modulos.
              </p>
            </div>
            <button
              type="button"
              className="btn management-btn-primary"
              onClick={handleSave}
              disabled={loading || saving}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>

        <div className="row g-4 justify-content-center">
          <div className="col-12">
            <div className="card shadow-sm border-0 sidebar-preview-card">
              <div className="card-header bg-white border-0">
                <h6 className="mb-0">Vista del sidebar</h6>
                <small className="text-muted">Arrastra para reordenar</small>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-muted">Cargando menus...</div>
                ) : (
                  <div className="sidebar-preview sidebar-preview--center">
                    <div className="sidebar-preview-brand">
                      <div className="brand-dot" />
                      <span>ADEMY</span>
                    </div>
                    <div className="sidebar-preview-nav">
                      {parents.map((parent) => (
                        <div
                          key={parent.id}
                          className={`sidebar-preview-group ${
                            dragState.id === parent.id ? "is-dragging" : ""
                          }`}
                          onDragOver={handleDragOver}
                          onDrop={() => handleDropOnGroup(parent.id)}
                          onDragEnd={resetDrag}
                        >
                          <div
                            className="sidebar-preview-title"
                            onDragOver={handleDragOver}
                            onDrop={() => handleDropOnParent(parent.id)}
                            draggable
                            onDragStart={(event) =>
                              handleDragStart(event, parent.id, null, "parent")
                            }
                          >
                            <span className="sidebar-preview-icon is-module">
                              <i className={parent.icon || "bi bi-folder"}></i>
                            </span>
                            <i className="bi bi-grip-vertical"></i>
                            <span>{parent.nombre}</span>
                          </div>
                          <div
                            className="sidebar-preview-children"
                            onDragOver={handleDragOver}
                            onDrop={() => handleDropOnGroup(parent.id)}
                          >
                            {(childrenMap.get(parent.id) || []).map((child) => (
                              <div
                                key={child.id}
                                className={`sidebar-preview-item ${
                                  dragState.id === child.id ? "is-dragging" : ""
                                }`}
                                draggable
                                onDragStart={(event) =>
                                  handleDragStart(event, child.id, parent.id, "child")
                                }
                                onDragOver={handleDragOver}
                                onDrop={() => handleDropOnChild(parent.id, child.id)}
                                onDragEnd={resetDrag}
                              >
                                <span className="sidebar-preview-icon is-child">
                                  <i className={child.icon || "bi bi-dot"}></i>
                                </span>
                                <i className="bi bi-grip-vertical"></i>
                                <span>{child.nombre}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
